import { BsdsInput } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { BsdsInputEvent } from '../../types/componentType';
import type { PhoneInputProps } from '../../types/formType';
import { formatPhone, validatePhone } from '../../utils/phoneUtils';

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onValidationChange,
  placeholder = "(11) 99999-9999",
  required = false,
  disabled = false,
  className = "",
  label,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  'aria-disabled': ariaDisabled,
  'aria-autocomplete': ariaAutocomplete,
  'aria-haspopup': ariaHasPopup,
  'aria-controls': ariaControls,
  'aria-owns': ariaOwns,
  'aria-activedescendant': ariaActiveDescendant,
  'aria-errormessage': ariaErrorMessage,
}) => {
  const [isValid, setIsValid] = useState<boolean>(true);
  const [hasBeenTouched, setHasBeenTouched] = useState<boolean>(false);
  const inputRef = useRef<HTMLBsdsInputElement>(null);

  // Determine error message
  const getErrorMessage = () => {

    const cleanPhoneValue = value.replace(/\D/g, '');

    if (hasBeenTouched && cleanPhoneValue.length === 11 && !isValid) {
      return 'Número de telefone inválido';
    }

    if (hasBeenTouched && required && cleanPhoneValue.length === 0) {
      return 'Telefone é obrigatório';
    }

    return '';
  };

  const errorMessage = getErrorMessage();

  // Function to update validation classes on the BsdsInput element
  const updateValidationClasses = useCallback(() => {
    if (inputRef.current) {
      const element = inputRef.current;

      // Add bsds-touched class when user has interacted
      if (hasBeenTouched) {
        element.classList.add('bsds-touched');
      } else {
        element.classList.remove('bsds-touched');
      }

      // Add bsds-invalid class when there's an error
      if (hasBeenTouched && !isValid && errorMessage) {
        element.classList.add('bsds-invalid');
      } else {
        element.classList.remove('bsds-invalid');
      }
    }
  }, [hasBeenTouched, isValid, errorMessage]);

  // Handle input change with formatting (onChange event - blur)
  const handleChange = useCallback((event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLBsdsInputElement;
    const newValue = (event as BsdsInputEvent).detail?.value || target?.value || event;
    // Ensure newValue is a string before formatting
    const stringValue = typeof newValue === 'string' ? newValue : String(newValue || '');
    // Apply phone formatting
    const formattedValue = formatPhone(stringValue);
    onChange(formattedValue);
    setHasBeenTouched(true);
  }, [onChange]);

  // Handle input change with formatting (onInput event - real time)
  const handleInput = useCallback((event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLBsdsInputElement;
    const newValue = (event as BsdsInputEvent).detail?.value || target?.value || event;
    // Ensure newValue is a string before formatting
    const stringValue = typeof newValue === 'string' ? newValue : String(newValue || '');
    // Apply phone formatting
    const formattedValue = formatPhone(stringValue);
    onChange(formattedValue);
    setHasBeenTouched(true);
  }, [onChange]);

  // Validate phone when value changes
  useEffect(() => {
    const cleanPhoneValue = value.replace(/\D/g, '');

    if (cleanPhoneValue.length === 11) {
      const valid = validatePhone(value);
      setIsValid(valid);
      onValidationChange?.(valid);
    } else if (cleanPhoneValue.length === 0) {
      // Empty value - valid if not required, invalid if required
      const valid = !required;
      setIsValid(valid);
      onValidationChange?.(valid);
    } else {
      // Incomplete phone - don't show error while typing
      setIsValid(true);
      onValidationChange?.(false);
    }
  }, [value, onValidationChange, required]);

  // Update validation classes when state changes
  useEffect(() => {
    updateValidationClasses();
  }, [updateValidationClasses]);

  return (
    <BsdsInput
      ref={inputRef}
      value={value}
      label={label}
      onChange={handleChange}
      onInput={handleInput}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className={className}
      maxlength={15}
      type="tel"
      inputMode="tel"
      errorText={errorMessage}
      hasHintIcon
      color="primary"
      clearInput={false}
      platform="web"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      aria-required={ariaRequired}
      aria-disabled={ariaDisabled}
      aria-autocomplete={ariaAutocomplete}
      aria-haspopup={ariaHasPopup}
      aria-controls={ariaControls}
      aria-owns={ariaOwns}
      aria-activedescendant={ariaActiveDescendant}
      aria-errormessage={ariaErrorMessage}
    />
  );
};

